import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Phone, MessageCircle, Clock, MapPin, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Booking = () => {
  const [formData, setFormData] = useState({
    nom_complet: '',
    telephone: '',
    email: '',
    prestation: '',
    date_souhaitee: '',
    heure_souhaitee: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'Coupe Homme - 15€',
    'Coupe + Barbe - 25€',
    'Tresses Femmes - 40€',
    'Box Braids - 60€',
    'Ongles Gel - 25€',
    'Manucure Complète - 35€'
  ];

  const heures = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
    '21:00', '21:30', '22:00'
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nom_complet || !formData.telephone || !formData.prestation || !formData.date_souhaitee || !formData.heure_souhaitee) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);

    try {
      // Envoyer directement sur WhatsApp de Pablo
      const dateFormatted = new Date(formData.date_souhaitee).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      const reservationMessage = `🎯 *NOUVELLE RÉSERVATION*
*Pablo Coiffure - Site Web*

👤 *CLIENT*
Nom: ${formData.nom_complet}
Tél: ${formData.telephone}${formData.email ? `\nEmail: ${formData.email}` : ''}

✂️ *DEMANDE DE RDV*
Service: ${formData.prestation}
Date: ${dateFormatted}
Heure: ${formData.heure_souhaitee}${formData.message ? `\n\n💬 Message du client:\n"${formData.message}"` : ''}

⚡ *À FAIRE:* Rappeler le client pour confirmer

📍 Pablo Coiffure
94 Grande Rue de la Guillotière, Lyon 7ème`;
      
      // Ouvrir WhatsApp avec le message pré-rempli pour Pablo
      const whatsappUrl = `https://wa.me/33767632259?text=${encodeURIComponent(reservationMessage)}`;
      window.open(whatsappUrl, '_blank');
      
      // Attendre un peu pour que l'utilisateur voie que ça s'ouvre
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success('✅ Votre demande a été envoyée à Pablo sur WhatsApp ! Il vous rappellera très rapidement pour confirmer votre rendez-vous.');
      
      // Reset form
      setFormData({
        nom_complet: '',
        telephone: '',
        email: '',
        prestation: '',
        date_souhaitee: '',
        heure_souhaitee: '',
        message: ''
      });
    } catch (error) {
      console.error('Erreur lors de la réservation:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Bonjour Pablo, je souhaite prendre rendez-vous pour ${formData.prestation || 'un service'} le ${formData.date_souhaitee || '[date]'} à ${formData.heure_souhaitee || '[heure]'}. Mon nom : ${formData.nom_complet || '[nom]'}`
    );
    window.open(`https://wa.me/33767632259?text=${message}`, '_blank');
  };

  const handleCall = () => {
    window.open('tel:+33767632259', '_blank');
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
            Prendre <span className="text-gradient">Rendez-vous</span>
          </h1>
          <p className="text-xl text-muted-foreground">
            Réservez votre créneau avec Pablo en quelques clics
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Formulaire de réservation */}
          <div className="lg:col-span-2">
            <Card className="card-luxury">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-6 w-6 text-primary" />
                  <span>Formulaire de Réservation</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Informations personnelles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nom_complet">Nom complet *</Label>
                      <Input
                        id="nom_complet"
                        type="text"
                        value={formData.nom_complet}
                        onChange={(e) => handleInputChange('nom_complet', e.target.value)}
                        placeholder="Votre nom et prénom"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input
                        id="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        placeholder="06 12 34 56 78"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email (optionnel)</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="votre@email.com"
                    />
                  </div>

                  {/* Service et horaires */}
                  <div className="space-y-2">
                    <Label htmlFor="prestation">Service souhaité *</Label>
                    <Select value={formData.prestation} onValueChange={(value) => handleInputChange('prestation', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisissez votre service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date_souhaitee">Date souhaitée *</Label>
                      <Input
                        id="date_souhaitee"
                        type="date"
                        value={formData.date_souhaitee}
                        onChange={(e) => handleInputChange('date_souhaitee', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="heure_souhaitee">Heure souhaitée *</Label>
                      <Select value={formData.heure_souhaitee} onValueChange={(value) => handleInputChange('heure_souhaitee', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisissez l'heure" />
                        </SelectTrigger>
                        <SelectContent>
                          {heures.map((heure) => (
                            <SelectItem key={heure} value={heure}>
                              {heure}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message (optionnel)</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Précisions sur votre demande..."
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Envoyer ma demande
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact direct */}
          <div className="space-y-6">
            <Card className="card-luxury">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Phone className="h-6 w-6 text-primary" />
                  <span>Contact Direct</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground text-sm">
                  Besoin d'une réponse immédiate ? Contactez Pablo directement :
                </p>
                
                <Button onClick={handleCall} className="w-full bg-green-600 hover:bg-green-700">
                  <Phone className="h-4 w-4 mr-2" />
                  Appeler maintenant
                </Button>
                
                <Button onClick={handleWhatsApp} className="w-full bg-green-500 hover:bg-green-600">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
              </CardContent>
            </Card>

            <Card className="card-luxury">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-6 w-6 text-primary" />
                  <span>Informations</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-medium">Adresse</p>
                  <p className="text-sm text-muted-foreground">
                    94 Grande Rue de la Guillotière<br />
                    69007 Lyon
                  </p>
                </div>
                
                <div>
                  <p className="font-medium">Horaires</p>
                  <p className="text-sm text-muted-foreground">
                    Lundi au Samedi<br />
                    <span className="text-primary font-medium">Ouvert 24h/24</span>
                  </p>
                </div>
                
                <div>
                  <p className="font-medium">Téléphone</p>
                  <p className="text-sm text-muted-foreground">
                    07 67 63 22 59
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;